import React, { useEffect, useRef, useState } from 'react';
import { User } from '../../../api/usersApi';
import UpdateUserModal from '../update-user/UpdateUserModal';
import VinculoNasajonDoUsuario from '../update-other-user/VinculoNasajonDoUsuario';
import { PERFIS_DISPONIVEIS } from '../perfis-adicionais/PerfisAdicionais';
import '../update-user/UpdateUserModal.css';
import '../../../shared/ui/badge-status.css';

interface UsersMyInfoProps {
  isOpen: boolean;
  onCancel: () => void;
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

/** Rótulo de tela de um papel: aceita o enum cru (`papeis`) e o nome de exibição (`userType`). */
const rotuloDoPapel = (papel: string): string =>
  PERFIS_DISPONIVEIS.find((p) => p.valor === papel || p.rotulo === papel)?.rotulo ?? papel;

const iniciais = (nome: string): string =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? '')
    .join('') || '?';

const dataPtBr = (iso?: string | null): string => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '-');

/**
 * "Minhas informações" — a ficha de quem está logado.
 *
 * Refeita em 18/10/2026 (pedido do Victor: "muito pobre"). Dois defeitos saíram junto:
 * "Nome de Usuário" mostrava o NOME COMPLETO, e "Administrador" lia a flag legada `isAdmin`, que
 * não tem semântica desde 07/2026 — quem é administrador é quem TEM o papel ADMIN, e isso agora
 * aparece nos perfis.
 *
 * ⚠️ Os perfis são os EFETIVOS (`papeis` de `GET /users/:id`, que já soma os dos grupos de acesso)
 * — é o que a pessoa alcança de fato, não só o que foi marcado nela.
 */
const UsersMyInfo: React.FC<UsersMyInfoProps> = ({ isOpen, onCancel, user, setUser, sidebarRef, isSidebarOpen }) => {
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar && !isUpdateModalOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef, isUpdateModalOpen]);

  if (!isOpen) return null;

  const principal = rotuloDoPapel(user.userType);
  const perfis = Array.from(new Set([principal, ...(user.papeis ?? []).map(rotuloDoPapel)]));
  const temCodigosDoContac = Boolean(user.codigoVoluntario || user.codigoRegional);

  const campo = (titulo: string, valor: string) => (
    <div className="Campo">
      <div className="CampoTitulo">{titulo}</div>
      <div className="CampoInput">
        <input className="Input" type="text" value={valor} title={valor} readOnly aria-label={titulo} />
      </div>
    </div>
  );

  return (
    <>
      <div className="edit-car-modal-overlay update-user-modal-specific">
        <div className="edit-car-modal-content" ref={modalRef}>
          <div className="lightbox-update-user">
            <div className="LightboxLogin">
              <div className="EditarCarro">Minhas informações</div>

              <div className="minhas-info-cabecalho">
                <div className="minhas-info-avatar" aria-hidden="true">{iniciais(user.fullName || user.username)}</div>
                <div className="minhas-info-identidade">
                  <div className="minhas-info-nome">{user.fullName || user.username}</div>
                  <div className="minhas-info-sub">
                    @{user.username} · {user.email}
                  </div>
                </div>
                <span className={`cilia-badge ${user.deletedAt ? 'badge-cinza' : 'badge-verde'}`}>
                  {user.deletedAt ? 'Inativo' : 'Ativo'}
                </span>
              </div>

              <div className="minhas-info-secao">
                <div className="CampoTitulo">Perfis de acesso</div>
                <div className="minhas-info-perfis">
                  {perfis.map((perfil) => (
                    <span
                      key={perfil}
                      className={`cilia-badge ${perfil === principal ? 'badge-azul' : 'badge-cinza'}`}
                      title={perfil === principal ? 'Perfil principal' : 'Perfil adicional ou de grupo de acesso'}
                    >
                      {perfil}
                      {perfil === principal ? ' (principal)' : ''}
                    </span>
                  ))}
                </div>
              </div>

              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  {campo('Nome de usuário', user.username || '')}
                  {campo('E-mail', user.email || '')}
                </div>
              </div>

              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  {campo('Cadastrado em', dataPtBr(user.createdAt))}
                  {campo('Última alteração do cadastro', dataPtBr(user.updatedAt))}
                </div>
              </div>

              {temCodigosDoContac && (
                <div className="Frame1000004010">
                  <div className="Frame1000003994">
                    {campo('Código do Voluntário', user.codigoVoluntario ? String(user.codigoVoluntario) : '-')}
                    {campo('Código da Regional', user.codigoRegional ? String(user.codigoRegional) : '-')}
                  </div>
                </div>
              )}

              <VinculoNasajonDoUsuario userId={user.id} />

              <div className="Frame1000003998">
                <div className="CtaBigEditar cancel" onClick={onCancel}>
                  <button className="CustomFileCancel" type="button">Fechar</button>
                </div>
                <button className="CtaBigEditar save" type="button" onClick={() => setIsUpdateModalOpen(true)}>
                  Editar informações
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UpdateUserModal
        isOpen={isUpdateModalOpen}
        onCancel={() => setIsUpdateModalOpen(false)}
        user={user}
        setUser={setUser}
        sidebarRef={sidebarRef}
        isSidebarOpen={isSidebarOpen}
      />
    </>
  );
};

export default UsersMyInfo;
